import React, { useEffect, useRef, useState } from 'react';
import { observer } from "mobx-react-lite";
import { autorun, reaction } from "mobx";
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../config/config";
// import { ApplicationStore } from "../../store/ApplicationStore";
import { CompositionStateStore } from "../../store/CompositionStateStore";
import { TimelineStore } from "../../store/TimelineStore";
import { TIMELINE_CONTROLS_ELEMENT_ID, TIMELINE_ELEMENT_ID, TIMELINE_TRACKS_CONTAINER_ID } from "./config/constants";
import { CompositionService } from "../../services/CompositionService";
import { convertUnitsToTime } from "../../utils/dom/convertUnitsToTime";
import { convertAbsTimeToUnits } from "../../utils/dom/convertTimeToUnits";
import './Timeline.styles.scss';
// import TrackDivider from "./components/TrackDivider/TrackDivider";
import Controls from "./components/Controls/Controls";
// import EmptyTimelineButton from "./components/EmptyTimelineButton/EmptyTimelineButton";
import CanvasTimeRuler from "./components/CanvasTimeRuler/CanvasTimeRuler";
import Playhead from "./components/Playhead/Playhead";
import TrackDivider from "./components/TrackDivider/TrackDivider";
import { Track } from "./components/Track/Track";
import {ApplicationStore} from "../../store/ApplicationStore";
import { ScriptLayerStore } from "../../store/ScriptLayerStore";
import { secondsToTimecode } from "../../utils/secondsToTimecode";
import { ContextMenuStore } from "../../store/ContextMenuStore";
import { beginScriptAddMediaFlow } from "../../utils/scriptAddMediaFlow";
import { findScriptSceneAtTimelineTime, getScriptScenes, isScriptSceneEmpty } from "../../utils/scriptSceneUtils";
import { dispatchDuplicateRangeToNewComposition } from "../../utils/duplicateRangeToNewCompositionEvent";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import {
    dispatchRecordIntoTranscriptRequest,
    resolveRecordIntoTranscriptCompositionId,
    resolveScriptLocalTimeForRecording,
    resolveTimelineRecordAnchor,
} from "../../../pages/V2Editor/V2TextEditor/recordIntoTranscriptRequest";

const MIN_ZOOM_PERCENT = 50;
const MAX_ZOOM_PERCENT = 1000;
const DEFAULT_VISIBLE_WINDOW_SEC = 35;
const ZOOM_WHEEL_SENSITIVITY = 0.1; // percent per pixel (after deltaMode normalization)
const ZOOM_WHEEL_MAX_DELTA_PERCENT = 100;
// Maximum zoom in terms of pixels per second (at 1000%).
// This is intentionally high; timeline virtualization keeps scroll widths bounded.
const MAX_PX_PER_SEC = 100_000;

// Maximum pixel width of the scrollable track-content window (excluding left pad).
// When the full timeline would exceed this, we virtualize by shifting TimelineStore.baseStartSec
// and recentering scrollLeft as the user pans.
const MAX_TRACK_CONTENT_WIDTH_PX = 2_000_000;

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

function getBaseStartFloorSec(): number {
    const v = Number(TimelineStore.baseStartFloorSec ?? 0);
    return Number.isFinite(v) ? v : 0;
}

function getStartLeadInSec(): number {
    return Math.max(0, -getBaseStartFloorSec());
}

function computePxPerSecForZoomPercent(zoomPercent: number, fitPxPerSec: number): number {
    if (!Number.isFinite(fitPxPerSec) || fitPxPerSec <= 0) return 1;
    if (zoomPercent <= MIN_ZOOM_PERCENT) return fitPxPerSec;

    const tLinear = (zoomPercent - MIN_ZOOM_PERCENT) / (MAX_ZOOM_PERCENT - MIN_ZOOM_PERCENT);
    const t = clamp(tLinear, 0, 1);

    // Curve: make zoom "ramp up" earlier than pure log interpolation (closer to Descript feel).
    const tCurved = Math.sqrt(t);

    const ratio = MAX_PX_PER_SEC / fitPxPerSec;
    if (!Number.isFinite(ratio) || ratio <= 1) return fitPxPerSec;
    return fitPxPerSec * Math.pow(ratio, tCurved);
}

function computeZoomPercentForPxPerSec(pxPerSec: number, fitPxPerSec: number): number {
    if (!Number.isFinite(fitPxPerSec) || fitPxPerSec <= 0) return MIN_ZOOM_PERCENT;
    if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return MIN_ZOOM_PERCENT;
    if (pxPerSec <= fitPxPerSec) return MIN_ZOOM_PERCENT;

    const ratio = MAX_PX_PER_SEC / fitPxPerSec;
    if (!Number.isFinite(ratio) || ratio <= 1) return MIN_ZOOM_PERCENT;

    const desiredRatio = pxPerSec / fitPxPerSec;
    if (!Number.isFinite(desiredRatio) || desiredRatio <= 1) return MIN_ZOOM_PERCENT;
    if (desiredRatio >= ratio) return MAX_ZOOM_PERCENT;

    const exponent = Math.log(desiredRatio) / Math.log(ratio);
    const t = clamp(exponent, 0, 1);
    const tLinear = t * t; // Invert the sqrt curve used in computePxPerSecForZoomPercent.
    return MIN_ZOOM_PERCENT + tLinear * (MAX_ZOOM_PERCENT - MIN_ZOOM_PERCENT);
}

interface TimelineProps {
    timelineHidden: boolean;
    toggleTimelineHide: () => void;
    disabled?: boolean;
}

// @ts-ignore
const Timeline = observer(({timelineHidden, toggleTimelineHide, disabled=false} : TimelineProps) => {
    const [playheadX, setPlayheadX] = useState(0);
    const [cursorTime, setCursorTime] = useState<number | null>(null);
    const [orderedLayerIds, setOrderedLayerIds] = useState<string[]>(() => CompositionStateStore.orderedLayerIds ?? []);
    // const [librettoClipLayers, setLibrettoClipLayers] = useState(CompositionStore.layerToLibrettoClips);
    const [zoom, setZoom] = useState(TimelineStore.zoom);
    const [duration, setDuration] = useState(CompositionStateStore.duration);
    
    // Get script layer ID (if exists) - used to render it separately at the bottom
    const scriptLayerId = (CompositionService.isInitialized() && (CompositionService.composition as any)?.scriptLayer)
        ? (CompositionService.composition as any).scriptLayer.layerId
        : null;

    const timelineContainerRef = useRef<HTMLDivElement | null>(null);
    const timelineTracksRef = useRef(null);
    const timelineTracksResizeObserver = useRef(null);
    const rafRef = useRef<number | null>(null);
    const lastMouseXRef = useRef<number | null>(null);
    const lastContextMenuTimeRef = useRef<number | null>(null);
    const isRecenteringRef = useRef(false);
    const wheelZoomRemainderRef = useRef(0);

    useEffect(() => {
        const disposeAutorun = autorun(() => {
            // This will re-run whenever any observable accessed inside changes
            // setLibrettoClipLayers(CompositionStore.layerToLibrettoClips);
            setOrderedLayerIds(CompositionStateStore.orderedLayerIds);
            setDuration(CompositionStateStore.duration);
            setZoom(TimelineStore.zoom);
        });

        return () => {
            disposeAutorun();
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        TimelineStore.setWidth(CompositionStateStore.duration * TIMELINE_RULER_TICK_WIDTH_PX);
    }, [duration]);

    useEffect(() => {
        addScrollSync();

        // @ts-ignore
        timelineTracksResizeObserver.current = new ResizeObserver(handleTimelineTracksResize);
        if (timelineTracksRef.current) {
            // @ts-ignore
            timelineTracksResizeObserver.current.observe(timelineTracksRef.current);
        }

        return () => {
            removeScrollSync();
            // @ts-ignore
            timelineTracksResizeObserver.current?.disconnect();
        };
    }, []);

    const handleScroll = () => {
        const container = timelineTracksRef.current as any;
        if (!container) return;

        const durationSec = Number(CompositionStateStore.duration ?? 0);
        const pxPerSec = TimelineStore.pxPerSec;
        const leftPadPx = TimelineStore.leftPadPx;
        const rightPadPx = TimelineStore.rightPadPx;
        const baseStartMinSec = getBaseStartFloorSec();
        const startLeadInSec = getStartLeadInSec();

        const scrollLeftPx = Math.max(0, Number(container.scrollLeft ?? 0));
        TimelineStore.setScrollLeftPx(scrollLeftPx);
        setPlayheadX(leftPadPx - scrollLeftPx);

        const dispatchTracksScrollLeft = (nextScrollLeftPx: number) => {
            const event = new CustomEvent('tracksScrollLeft', {
                bubbles: true,
                detail: nextScrollLeftPx,
            });
            container.dispatchEvent(event);
        };

        const fullTrackContentWidthPx = (durationSec + startLeadInSec) * pxPerSec + rightPadPx;
        const trackContentWidthPx = Math.min(fullTrackContentWidthPx, MAX_TRACK_CONTENT_WIDTH_PX);
        const isVirtualized = fullTrackContentWidthPx > trackContentWidthPx;

        // If not virtualized, ensure baseStartSec stays at 0.
        if (!isVirtualized) {
            if (Math.abs((TimelineStore.baseStartSec ?? baseStartMinSec) - baseStartMinSec) > 1e-6) {
                TimelineStore.setBaseStartSec(baseStartMinSec);
            }
            dispatchTracksScrollLeft(scrollLeftPx);
            return;
        }

        // Prevent feedback loops when we recenter scrollLeft programmatically.
        if (isRecenteringRef.current) {
            dispatchTracksScrollLeft(scrollLeftPx);
            return;
        }

        const viewportWidthPx = Number(container.clientWidth ?? 0);
        if (!Number.isFinite(viewportWidthPx) || viewportWidthPx <= 0 || pxPerSec <= 0) {
            dispatchTracksScrollLeft(scrollLeftPx);
            return;
        }

        const windowSec = trackContentWidthPx / pxPerSec;
        const viewDurationSec = durationSec + startLeadInSec + (TimelineStore.rightPadSeconds ?? 0);
        const baseStartMaxSec = Math.max(baseStartMinSec, viewDurationSec - windowSec);
        if (baseStartMaxSec <= baseStartMinSec + 1e-6) {
            TimelineStore.setBaseStartSec(baseStartMinSec);
            dispatchTracksScrollLeft(scrollLeftPx);
            return;
        }

        const trackScrollLeftPx = Math.max(0, scrollLeftPx - leftPadPx);
        const maxTrackScrollLeftPx = Math.max(0, trackContentWidthPx - viewportWidthPx);

        const thresholdPx = Math.min(viewportWidthPx, maxTrackScrollLeftPx / 2);
        const nearLeftEdge = trackScrollLeftPx < thresholdPx;
        const nearRightEdge = trackScrollLeftPx > maxTrackScrollLeftPx - thresholdPx;

        const baseStartSec = TimelineStore.baseStartSec ?? 0;
        const canShiftLeft = baseStartSec > baseStartMinSec + 1e-6;
        const canShiftRight = baseStartSec < baseStartMaxSec - 1e-6;

        if ((nearLeftEdge && canShiftLeft) || (nearRightEdge && canShiftRight)) {
            const anchorSecUnclamped = baseStartSec + (trackScrollLeftPx + viewportWidthPx / 2) / pxPerSec;
            const anchorSec = clamp(anchorSecUnclamped, 0, durationSec);

            const nextBaseStartSec = clamp(anchorSec - windowSec / 2, baseStartMinSec, baseStartMaxSec);
            const anchorOffsetPx = (anchorSec - nextBaseStartSec) * pxPerSec;
            const nextTrackScrollLeftPx = clamp(anchorOffsetPx - viewportWidthPx / 2, 0, maxTrackScrollLeftPx);
            const nextScrollLeftPx = leftPadPx + nextTrackScrollLeftPx;

            const baseChanged = Math.abs(nextBaseStartSec - baseStartSec) > 1e-6;
            const scrollChanged = Math.abs(nextScrollLeftPx - scrollLeftPx) > 0.5;

            if (baseChanged || scrollChanged) {
                isRecenteringRef.current = true;
                TimelineStore.setBaseStartSec(nextBaseStartSec);
                container.scrollLeft = nextScrollLeftPx;
                TimelineStore.setScrollLeftPx(nextScrollLeftPx);
                setPlayheadX(leftPadPx - nextScrollLeftPx);
                dispatchTracksScrollLeft(nextScrollLeftPx);
                window.requestAnimationFrame(() => {
                    isRecenteringRef.current = false;
                });
                return;
            }
        }

        dispatchTracksScrollLeft(scrollLeftPx);
    };

    const positionToTime = (targetSec: number, viewportX: number) => {
        const container = timelineTracksRef.current as any;
        if (!container) return;

        const durationSec = Number(CompositionStateStore.duration ?? 0);
        const pxPerSec = TimelineStore.pxPerSec;
        const leftPadPx = TimelineStore.leftPadPx;
        const rightPadPx = TimelineStore.rightPadPx;
        const baseStartMinSec = getBaseStartFloorSec();
        const startLeadInSec = getStartLeadInSec();
        const viewportWidthPx = Number(container.clientWidth ?? 0);

        if (!Number.isFinite(durationSec) || durationSec <= 0) return;
        if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return;
        if (!Number.isFinite(viewportWidthPx) || viewportWidthPx <= 0) return;

        const fullTrackContentWidthPx = (durationSec + startLeadInSec) * pxPerSec + rightPadPx;
        const trackContentWidthPx = Math.min(fullTrackContentWidthPx, MAX_TRACK_CONTENT_WIDTH_PX);
        const isVirtualized = fullTrackContentWidthPx > trackContentWidthPx;

        const windowSec = trackContentWidthPx / pxPerSec;
        const viewDurationSec = durationSec + startLeadInSec + (TimelineStore.rightPadSeconds ?? 0);
        const baseStartMaxSec = isVirtualized
            ? Math.max(baseStartMinSec, viewDurationSec - windowSec)
            : baseStartMinSec;
        const clampedTargetSec = clamp(Number.isFinite(targetSec) ? targetSec : 0, 0, durationSec);

        const safeViewportX = clamp(Number.isFinite(viewportX) ? viewportX : 0, 0, viewportWidthPx);

        let nextBaseStartSec = baseStartMinSec;
        if (isVirtualized && baseStartMaxSec > baseStartMinSec + 1e-6) {
            nextBaseStartSec = clamp(
                clampedTargetSec - safeViewportX / pxPerSec,
                baseStartMinSec,
                baseStartMaxSec,
            );
        }

        const maxTrackScrollLeftPx = Math.max(0, trackContentWidthPx - viewportWidthPx);
        const targetXpx = (clampedTargetSec - nextBaseStartSec) * pxPerSec;
        const nextTrackScrollLeftPx = clamp(targetXpx - safeViewportX, 0, maxTrackScrollLeftPx);
        let nextScrollLeftPx = leftPadPx + nextTrackScrollLeftPx;

        // UX: when jumping to the very start/end, preserve padding behavior.
        if (clampedTargetSec <= 1e-3) {
            nextBaseStartSec = baseStartMinSec;
            nextScrollLeftPx = 0;
        } else if (clampedTargetSec >= durationSec - 1e-3) {
            nextBaseStartSec = baseStartMaxSec;
            nextScrollLeftPx = leftPadPx + maxTrackScrollLeftPx;
        }

        isRecenteringRef.current = true;
        TimelineStore.setBaseStartSec(nextBaseStartSec);
        container.scrollLeft = nextScrollLeftPx;
        handleScroll();
        window.requestAnimationFrame(() => {
            isRecenteringRef.current = false;
        });
    };

    const ensureTimeVisible = (targetSec: number, mode: "playing" | "seeking") => {
        const container = timelineTracksRef.current as any;
        if (!container) return;

        const durationSec = Number(CompositionStateStore.duration ?? 0);
        const pxPerSec = TimelineStore.pxPerSec;
        const leftPadPx = TimelineStore.leftPadPx;
        const viewportWidthPx = Number(container.clientWidth ?? 0);
        if (!Number.isFinite(durationSec) || durationSec <= 0) return;
        if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return;
        if (!Number.isFinite(viewportWidthPx) || viewportWidthPx <= 0) return;

        const baseStartSec = TimelineStore.baseStartSec ?? 0;
        const scrollLeftPx = Math.max(0, Number(container.scrollLeft ?? 0));
        const trackScrollLeftPx = Math.max(0, scrollLeftPx - leftPadPx);

        const visibleStartSec = baseStartSec + trackScrollLeftPx / pxPerSec;
        const visibleEndSec = baseStartSec + (trackScrollLeftPx + viewportWidthPx) / pxPerSec;

        const clampedTargetSec = clamp(Number.isFinite(targetSec) ? targetSec : 0, 0, durationSec);
        if (mode === "playing") {
            // "Page scroll" behavior: let the playhead traverse (almost) the full viewport,
            // then advance the viewport and keep the playhead near the left.
            const playheadX = (clampedTargetSec - visibleStartSec) * pxPerSec;
            const triggerX = viewportWidthPx * 0.99;
            const anchorX = clamp(viewportWidthPx * 0.05, 0, 80);

            if (playheadX < 0 || playheadX > triggerX) {
                positionToTime(clampedTargetSec, anchorX);
            }
            return;
        }

        const visibleSpanSec = Math.max(0, visibleEndSec - visibleStartSec);
        const marginSec = visibleSpanSec * 0.05;
        if (clampedTargetSec < visibleStartSec + marginSec || clampedTargetSec > visibleEndSec - marginSec) {
            positionToTime(clampedTargetSec, viewportWidthPx * 0.5);
        }
    };

    const addScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.addEventListener("scroll", handleScroll);
    };

    const removeScrollSync = () => {
        // @ts-ignore
        timelineTracksRef.current?.removeEventListener("scroll", handleScroll);
    };

    // @ts-ignore
    const handleTimelineTracksResize = (entries) => {
        const width = entries[0]?.contentRect?.width;
        if (width) {
            TimelineStore.setTracksContainerWidth(width);
        }
    };

    const getTimelineTimeAtClientX = (clientX: number): number | null => {
        const container = timelineTracksRef.current as any;
        if (!container) return null;

        const durationSec = Number(CompositionStateStore.duration ?? 0);
        if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;

        const rect = container.getBoundingClientRect();
        const scrollLeft = container.scrollLeft ?? 0;
        const leftPadPx = TimelineStore.leftPadPx;

        const x = clientX - rect.left + scrollLeft - leftPadPx;
        const time = convertUnitsToTime(Math.max(0, Number.isFinite(x) ? x : 0));
        return clamp(time, 0, durationSec);
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        lastMouseXRef.current = event.clientX;
        if (rafRef.current != null) return;

        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            const x = lastMouseXRef.current;
            if (x == null) return;
            const time = getTimelineTimeAtClientX(x);
            setCursorTime(time);
        });
    };

    const handleMouseLeave = () => {
        lastMouseXRef.current = null;
        setCursorTime(null);
        if (rafRef.current != null) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

		    const handleTimelineContextMenu = (event: React.MouseEvent) => {
		        event.preventDefault();
		        event.stopPropagation();

		        // Range tool UX: do not seek on right-click, and when a range exists show ONLY range actions.
		        if (ApplicationStore.rangeToolEnabled) {
		            const selectedClipId = ApplicationStore.selectedClipId;
		            if (selectedClipId) {
		                const selectedClip: any = CompositionService.getClipById(selectedClipId);
		                const isScriptSelected =
		                    Boolean(
		                        selectedClip &&
		                            (String(selectedClip.type) === "script" ||
		                                CompositionService.isScriptLayerClip?.(selectedClipId))
		                    );
		                const range = isScriptSelected
		                    ? ApplicationStore.selectedScriptLayerRange
		                    : ApplicationStore.selectedRange;

		                if (range) {
		                    const start = Math.min(Number(range.start ?? 0), Number(range.end ?? 0));
		                    const end = Math.max(Number(range.start ?? 0), Number(range.end ?? 0));
		                    const EPS = 1e-3;

		                    const items: any[] = [];

		                    if (isScriptSelected) {
		                        if (Number.isFinite(start) && Number.isFinite(end) && Math.abs(end - start) > EPS) {
		                            items.push({
		                                title: "Duplicate to new composition",
		                                onClick: () => {
		                                    dispatchDuplicateRangeToNewComposition({
		                                        startSec: start,
		                                        endSec: end,
		                                        source: "timeline-range-menu",
		                                    });
		                                    ContextMenuStore.hide();
		                                },
		                            });
		                        }

		                        const mapTimelineToSource = (t: number): number | null => {
		                            try {
		                                let src = ScriptLayerStore.mapTimelineTimeToSourceTime(t);
		                                if (src != null && Number.isFinite(src)) return Number(src);

		                                const scriptClip: any = CompositionService.getScriptLayerClip?.();
		                                if (!scriptClip) return null;

		                                const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
		                                const clipStartSec = Number(scriptClip.start?.seconds ?? 0);
		                                const absCompTime = clipStartSec + t * rate;
		                                const segs: any[] = Array.isArray(scriptClip.scriptSegments)
		                                    ? scriptClip.scriptSegments
		                                    : [];
		                                const nextSeg = segs.find(
		                                    (s: any) =>
		                                        Number(s?.compStartSec ?? 0) > absCompTime + EPS
		                                );
		                                if (nextSeg && Number.isFinite(nextSeg.srcStartSec)) {
		                                    return Number(nextSeg.srcStartSec);
		                                }
		                            } catch {}
		                            return null;
		                        };

		                        const s = mapTimelineToSource(start);
		                        const e2 = mapTimelineToSource(end);
		                        if (
		                            s != null &&
		                            e2 != null &&
		                            Number.isFinite(s) &&
		                            Number.isFinite(e2) &&
		                            Math.abs(e2 - s) > EPS
		                        ) {
		                            items.push(
		                                {
		                                    title: "Replace with gap clip",
		                                    onClick: () => {
		                                        CompositionService.addScriptLayerGap(s, e2);
		                                        ApplicationStore.setSelectedScriptLayerRange(null);
		                                        ContextMenuStore.hide();
		                                    },
		                                },
		                                {
		                                    title: "Delete",
		                                    onClick: () => {
		                                        CompositionService.cutScriptLayerRange(s, e2, { transcriptMode: "delete" });
		                                        ApplicationStore.setSelectedScriptLayerRange(null);
		                                        ContextMenuStore.hide();
		                                    },
		                                }
		                            );
		                        } else {
		                            items.push(
		                                { title: "Replace with gap clip", onClick: () => ContextMenuStore.hide() },
		                                { title: "Delete", onClick: () => ContextMenuStore.hide() }
		                            );
		                        }
		                    } else {
		                        const storeDur = CompositionStateStore.clips?.[selectedClipId]?.trimmedDuration;
		                        const dur =
		                            typeof storeDur === "number" && Number.isFinite(storeDur) && storeDur > 0
		                                ? storeDur
		                                : Number(CompositionStateStore.clips?.[selectedClipId]?.duration ?? 0);
		                        const clipStart = Number(CompositionStateStore.clips?.[selectedClipId]?.startTime ?? 0);

		                        const clampedStartRel = Math.max(0, Math.min(dur, start));
		                        const clampedEndRel = Math.max(0, Math.min(dur, end));
		                        const absStart = clipStart + Math.min(clampedStartRel, clampedEndRel);
		                        const absEnd = clipStart + Math.max(clampedStartRel, clampedEndRel);

		                        if (
		                            Number.isFinite(absStart) &&
		                            Number.isFinite(absEnd) &&
		                            Math.abs(absEnd - absStart) > EPS
		                        ) {
		                            items.push(
		                                {
		                                    title: "Replace with gap clip",
		                                    onClick: () => {
		                                        void CompositionService.replaceMediaRangeWithGap(
		                                            selectedClipId,
		                                            absStart,
		                                            absEnd
		                                        );
		                                        ApplicationStore.setSelectedRange(null);
		                                        ContextMenuStore.hide();
		                                    },
		                                },
		                                {
		                                    title: "Delete",
		                                    onClick: () => {
		                                        CompositionService.trimClip(selectedClipId, absStart, absEnd);
		                                        ApplicationStore.setSelectedRange(null);
		                                        ContextMenuStore.hide();
		                                    },
		                                }
		                            );
		                        } else {
		                            items.push(
		                                { title: "Replace with gap clip", onClick: () => ContextMenuStore.hide() },
		                                { title: "Delete", onClick: () => ContextMenuStore.hide() }
		                            );
		                        }
		                    }

		                    ContextMenuStore.show(event.clientX, event.clientY, items);
		                    return;
		                }
		            }
		        }

		        const time = getTimelineTimeAtClientX(event.clientX);
		        if (time != null) {
		            lastContextMenuTimeRef.current = time;
		            if (!ApplicationStore.rangeToolEnabled) {
		                void CompositionService.seek(time);
		            }
		            setCursorTime(time);
		        }

		        const menuAbsTime = Number(lastContextMenuTimeRef.current ?? CompositionStateStore.currentTime ?? 0);
	        const selectedClipId = ApplicationStore.selectedClipId;
	        const selectedTransitionId = ApplicationStore.selectedTransitionId;

	        const buildLayerOrderSubmenu = (clipId: string, sourceIndex?: number) => ({
	            title: "Layer order",
	            children: [
	                { title: "Bring to front", onClick: () => { CompositionService.bringVisualToFront(clipId, sourceIndex); ContextMenuStore.hide(); } },
	                { title: "Bring forward", onClick: () => { CompositionService.bringVisualForward(clipId, sourceIndex); ContextMenuStore.hide(); } },
	                { title: "Send backward", onClick: () => { CompositionService.sendVisualBackward(clipId, sourceIndex); ContextMenuStore.hide(); } },
	                { title: "Send to back", onClick: () => { CompositionService.sendVisualToBack(clipId, sourceIndex); ContextMenuStore.hide(); } },
	            ],
	        });

	        const getScriptClipLocalTimeAtMenu = (): number | null => {
	            const clip: any = CompositionService.getScriptLayerClip?.();
	            return resolveScriptLocalTimeForRecording(clip, menuAbsTime);
	        };

	        const getSceneStartAtMenu = (): number => {
	            try {
	                const clip: any = CompositionService.getScriptLayerClip?.();
	                if (!clip) return 0;
	                const EPS = 1e-3;
	                const inSec = Number(clip.range?.[0]?.seconds ?? 0);
	                const outSec = Number(clip.range?.[1]?.seconds ?? clip.duration?.seconds ?? inSec);
	                const sceneSplitsSrc: number[] = (clip.splits ?? [])
	                    .filter((s: any) => s?.isSceneBoundary === true)
	                    .map((s: any) => Number(s.timeSec))
	                    .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
	                    .sort((a: number, b: number) => a - b);

	                const clipStartSec = Number(clip.start?.seconds ?? 0);
	                let srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(
	                    menuAbsTime - clipStartSec,
	                );
	                if (srcTime == null) {
	                    const segs: any[] = Array.isArray(clip.scriptSegments) ? clip.scriptSegments : [];
	                    const nextSeg = segs.find(
	                        (s: any) => Number(s?.compStartSec ?? 0) > menuAbsTime + EPS,
	                    );
	                    if (nextSeg && Number.isFinite(nextSeg.srcStartSec)) {
	                        srcTime = Number(nextSeg.srcStartSec);
	                    }
	                }
	                if (srcTime == null || !Number.isFinite(srcTime)) return 0;

	                let prevSplit = inSec;
	                for (const t of sceneSplitsSrc) {
	                    if (t <= (srcTime as number) + EPS) prevSplit = t;
	                    else break;
	                }
	                const mapped = ScriptLayerStore.mapSourceTimeToCompositionTime(prevSplit);
	                return mapped != null && Number.isFinite(mapped) ? Number(mapped) : 0;
	            } catch {
	                return 0;
	            }
	        };

	        const getSceneAtMenu = (): { index: number; startSec: number; endSec: number; isEmpty: boolean } | null => {
	            const scriptClip: any = CompositionService.getScriptLayerClip?.();
	            if (!scriptClip) return null;
	            const scene = findScriptSceneAtTimelineTime(scriptClip, menuAbsTime, 1e-3);
	            if (!scene) return null;
	            return {
	                index: Number(scene.index),
	                startSec: Number(scene.startSec),
	                endSec: Number(scene.endSec),
	                isEmpty: isScriptSceneEmpty(scriptClip, scene),
	            };
	        };

	        const buildRecordIntoTranscriptMenuItem = (): any | null => {
	            if (ApplicationStore.rangeToolEnabled) return null;
	            const scriptClip: any = CompositionService.getScriptLayerClip?.();
	            const requestedScriptLocalSec = getScriptClipLocalTimeAtMenu();
	            if (!scriptClip || requestedScriptLocalSec == null) return null;

	            const scriptClipId = String(scriptClip?.id ?? "") || null;
	            const transcriptWords = (ScriptLayerStore.transcriptWords ?? []).map((word: any) => ({
	                text: String(word?.text ?? ""),
	                payload: { ...word, clipId: scriptClipId ?? undefined },
	            }));
	            const anchor = resolveTimelineRecordAnchor(requestedScriptLocalSec, transcriptWords);
	            if (!anchor) return null;

	            const projectId = ProjectAssetsStore.projectId;
	            let engineCompositionId: string | null = null;
	            try {
	                engineCompositionId = (CompositionService.composition as any)?.compositionId ?? null;
	            } catch {}
	            const compositionId = resolveRecordIntoTranscriptCompositionId({
	                engineCompositionId,
	                projectCompositionId: ProjectAssetsStore.projectCurrentCompositionId,
	            }) || null;

	            const dispatch = (mediaKind: "audio" | "video") => {
	                dispatchRecordIntoTranscriptRequest({
	                    source: "timeline-context-menu",
	                    mediaKind,
	                    projectId,
	                    compositionId,
	                    scriptClipId,
	                    anchor,
	                });
	                ContextMenuStore.hide();
	            };

	            return {
	                title: "Record into transcript",
	                children: [
	                    { title: "Audio", onClick: () => dispatch("audio") },
	                    { title: "Video", onClick: () => dispatch("video") },
	                ],
	            };
	        };
	        const recordIntoTranscriptMenuItem = buildRecordIntoTranscriptMenuItem();

	        // ────────────────────────────────────────────────────────────────
	        // Case C: nothing selected on the timeline (no clip or transition).
	        // ────────────────────────────────────────────────────────────────
	        if (selectedClipId == null && selectedTransitionId == null) {
	            const items: any[] = [
	                {
	                    title: "Create scene break here",
	                    onClick: () => {
	                        const clipLocal = getScriptClipLocalTimeAtMenu();
	                        if (clipLocal != null) {
	                            CompositionService.splitScriptLayer(clipLocal, true);
	                        }
	                        ContextMenuStore.hide();
	                    },
	                },
	                {
	                    title: "Add layer",
	                    onClick: () => {
	                        try {
	                            const startAtSec = menuAbsTime;
	                            ApplicationStore.setPendingAddLayerContext({ startAtSec, constrainToSceneEnd: true });
	                        } catch {}
	                        try {
	                            window.dispatchEvent(new CustomEvent('openProjectSidebarFiles', { detail: { source: 'timeline' } }));
	                        } catch {}
	                        ContextMenuStore.hide();
	                    },
	                },
	            ];
	            if (recordIntoTranscriptMenuItem) items.unshift(recordIntoTranscriptMenuItem);

	            const menuScene = getSceneAtMenu();
	            if (menuScene?.isEmpty) {
	                items.unshift({
	                    title: "Add media",
	                    onClick: () => {
	                        beginScriptAddMediaFlow({
	                            atClipLocalSec: Number(menuScene.startSec ?? 0),
	                            preferExistingEmptyScene: true,
	                            source: "timeline-empty-scene",
	                            sceneIndex: Number(menuScene.index ?? 0),
	                            sceneStartSec: Number(menuScene.startSec ?? 0),
	                            sceneEndSec: Number(menuScene.endSec ?? 0),
	                        });
	                        ContextMenuStore.hide();
	                    },
	                });
	            }

	            ContextMenuStore.show(event.clientX, event.clientY, items);
	            return;
	        }

	        // Transition actions are not implemented yet, but recording still targets the clicked background time.
	        if (selectedTransitionId != null) {
	            if (recordIntoTranscriptMenuItem) {
	                ContextMenuStore.show(event.clientX, event.clientY, [recordIntoTranscriptMenuItem]);
	            }
	            return;
	        }
	        if (!selectedClipId) return;

	        const selectedClip: any = CompositionService.getClipById(selectedClipId);
	        const isScriptSelected =
	            Boolean(selectedClip && (String(selectedClip.type) === "script" || CompositionService.isScriptLayerClip?.(selectedClipId)));
	        const isVisualSelected = CompositionService.isVisualClip(selectedClip);
	        const selectedSourceIndex = ApplicationStore.selectedVisualElement?.clipId === selectedClipId
	            ? ApplicationStore.selectedVisualElement?.sourceIndex
	            : undefined;
	        const hasSelectedSourceIndex = typeof selectedSourceIndex === "number" && Number.isFinite(selectedSourceIndex);

	        const sceneStartAtMenu = getSceneStartAtMenu();

	        // ────────────────────────────────────────────────────────────────
	        // Case A: a non-script clip is selected
	        // ────────────────────────────────────────────────────────────────
	        if (!isScriptSelected) {
	            const items: any[] = [
	                {
	                    title: "Add scene",
	                    onClick: () => {
	                        const clipLocal = getScriptClipLocalTimeAtMenu();
	                        if (clipLocal != null) {
	                            CompositionService.splitScriptLayer(clipLocal, true);
	                        }
	                        ContextMenuStore.hide();
	                    },
	                },
	                {
	                    title: "Add layer",
	                    onClick: () => {
	                        try {
	                            ApplicationStore.setPendingAddLayerContext({ startAtSec: sceneStartAtMenu, constrainToSceneEnd: true });
	                        } catch {}
	                        try {
	                            window.dispatchEvent(new CustomEvent('openProjectSidebarFiles', { detail: { source: 'timeline' } }));
	                        } catch {}
	                        ContextMenuStore.hide();
	                    },
	                },
	            ];
	            if (recordIntoTranscriptMenuItem) items.unshift(recordIntoTranscriptMenuItem);

	            if (isVisualSelected) {
	                items.push(buildLayerOrderSubmenu(selectedClipId));
	            }

	            items.push(
	                {
	                    title: "Split clip",
	                    onClick: () => {
	                        CompositionService.splitClip(menuAbsTime);
	                        ContextMenuStore.hide();
	                    },
	                },
	                {
	                    title: "Delete clip",
	                    onClick: () => {
	                        CompositionService.deleteClipById(selectedClipId);
	                        ApplicationStore.clearSelectedClipId();
	                        ContextMenuStore.hide();
	                    },
	                }
	            );

	            ContextMenuStore.show(event.clientX, event.clientY, items);
	            return;
	        }

	        // ────────────────────────────────────────────────────────────────
	        // Case B: the script clip (a scene) is selected
	        // ────────────────────────────────────────────────────────────────
	        const items: any[] = [
	            {
	                title: "Add a scene break here",
	                onClick: () => {
	                    const clipLocal = getScriptClipLocalTimeAtMenu();
	                    if (clipLocal != null) {
	                        CompositionService.splitScriptLayer(clipLocal, true);
	                    }
	                    ContextMenuStore.hide();
	                },
	            },
	            {
	                title: "Add layer",
	                onClick: () => {
	                    try {
	                        ApplicationStore.setPendingAddLayerContext({ startAtSec: sceneStartAtMenu, constrainToSceneEnd: true });
	                    } catch {}
	                    try {
	                        window.dispatchEvent(new CustomEvent('openProjectSidebarFiles', { detail: { source: 'timeline' } }));
	                    } catch {}
	                    ContextMenuStore.hide();
	                },
	            },
	        ];
	        if (recordIntoTranscriptMenuItem) items.unshift(recordIntoTranscriptMenuItem);

	        const selectedSceneForAddMedia = (() => {
	            try {
	                const idx = Number(ApplicationStore.selectedSceneIndex ?? NaN);
	                if (!Number.isFinite(idx) || idx < 0) return null;
	                const scriptClip: any = CompositionService.getScriptLayerClip?.();
	                if (!scriptClip) return null;
	                const scene =
	                    getScriptScenes(scriptClip).find((candidate) => Number(candidate.index) === idx) ?? null;
	                if (!scene) return null;
	                return {
	                    index: idx,
	                    startSec: Number(scene.startSec ?? 0),
	                    endSec: Number(scene.endSec ?? 0),
	                    isEmpty: isScriptSceneEmpty(scriptClip, scene),
	                };
	            } catch {
	                return null;
	            }
	        })();

	        if (selectedSceneForAddMedia?.isEmpty) {
	            items.push({
	                title: "Add media",
	                onClick: () => {
	                    beginScriptAddMediaFlow({
	                        atClipLocalSec: Number(selectedSceneForAddMedia.startSec ?? 0),
	                        preferExistingEmptyScene: true,
	                        source: "timeline-selected-empty-scene",
	                        sceneIndex: Number(selectedSceneForAddMedia.index ?? 0),
	                        sceneStartSec: Number(selectedSceneForAddMedia.startSec ?? 0),
	                        sceneEndSec: Number(selectedSceneForAddMedia.endSec ?? 0),
	                    });
	                    ContextMenuStore.hide();
	                },
	            });
	        }

		        if (isVisualSelected && hasSelectedSourceIndex) {
		            items.push(buildLayerOrderSubmenu(selectedClipId, selectedSourceIndex));
		        }

			        items.push(
			            {
			                title: "Add gap",
			                onClick: () => {
			                    const sceneEndClipLocal = (() => {
			                        try {
			                            const idx = ApplicationStore.selectedSceneIndex;
			                            if (typeof idx !== "number" || !Number.isFinite(idx) || idx < 0) return null;
			                            const scenes: any[] = Array.isArray((selectedClip as any)?.getScenes?.())
			                                ? (selectedClip as any).getScenes()
			                                : [];
			                            const scene =
			                                scenes.find((s: any) => Number(s?.index ?? NaN) === idx) ??
			                                scenes[idx];
			                            const end = Number(scene?.endSec ?? NaN);
			                            return Number.isFinite(end) ? end : null;
			                        } catch {
			                            return null;
			                        }
			                    })();
			                    const clipLocal = sceneEndClipLocal ?? getScriptClipLocalTimeAtMenu();
			                    if (clipLocal != null && Number.isFinite(clipLocal)) {
			                        CompositionService.insertEmptyScriptScene(clipLocal, 5);
			                    }
			                    ContextMenuStore.hide();
			                },
			            },
		            {
		                title: "Delete",
		                onClick: () => {
		                    const idx = ApplicationStore.selectedSceneIndex;
	                    if (typeof idx === "number" && Number.isFinite(idx) && idx >= 0) {
	                        const scenes: any[] = Array.isArray((selectedClip as any)?.getScenes?.())
	                            ? (selectedClip as any).getScenes()
	                            : [];
	                        if (scenes.length <= 1) {
	                            CompositionService.deleteClipById(selectedClipId);
	                        } else {
	                            CompositionService.deleteScriptLayerScene(idx, { transcriptMode: "delete" });
	                        }
	                    }
	                    ContextMenuStore.hide();
	                },
	            }
	        );

	        ContextMenuStore.show(event.clientX, event.clientY, items);
	    };

    // @ts-ignore
    // const handleCreateLayerMedia = async (event, mediaId, index) => {
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const layer = CompositionService.createLayer(index);
    //     const clip = await CompositionService.addMediaToLayer(layer.id, mediaId, startTime);
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    // @ts-ignore
    // const handleCreateLayerText = async (event, title, index) => {
    //     const layer = CompositionService.createLayer(index);
    //     const startTime = convertUnitsToTime(event.nativeEvent.offsetX);
    //     const clip = await CompositionService.createLottieClip({
    //         dataUrl: title.dataUrl,
    //         propertiesUrl: title.propertiesUrl,
    //         startTime: startTime,
    //         layerId: layer.id,
    //     });
    //     if (clip) {
    //         // let librettoClipId = clip.getCustomData(CLIP_LIBRETTO_CLIP_ID) as string;
    //         // if (librettoClipId === null || librettoClipId === "" || librettoClipId === undefined) {
    //         //     librettoClipId = GenerateLibrettoClipId();
    //         //     clip.setCustomData(CLIP_LIBRETTO_CLIP_ID, librettoClipId);
    //         // }
    //         //ApplicationStore.setSelectedLibrettoClipId(librettoClipId);
    //     }
    // };

    // @ts-ignore
    const renderTrackDivider = (index) => {
        const newIndex = allLayersOrder.length - index;
        return (
            <TrackDivider
                index={newIndex}
                // @ts-ignore
                onDropMediaClip={(event, mediaId) => {}}
                // @ts-ignore
                onDropTextClip={(event, title) => {}}
            />
        );
    };

    useEffect(() => {
        const disposeZoom = reaction(
            () => ({
                zoomPercent: TimelineStore.zoomPercent,
                durationSec: CompositionStateStore.duration,
                compositionDurationSec: CompositionStateStore.compositionDuration,
                containerWidthPx: TimelineStore.tracksContainerWidth,
                leftPadPx: TimelineStore.leftPadPx,
                rightPadSeconds: TimelineStore.rightPadSeconds,
            }),
            ({ zoomPercent, durationSec, compositionDurationSec, containerWidthPx, leftPadPx, rightPadSeconds }) => {
                const safeDurationSec = Number(durationSec ?? 0);
                if (!Number.isFinite(safeDurationSec) || safeDurationSec <= 0) return;

                const safeContainerWidthPx = Number(containerWidthPx ?? 0);
                if (!Number.isFinite(safeContainerWidthPx) || safeContainerWidthPx <= 0) return;

                const safeLeftPadPx = Math.max(0, Number.isFinite(leftPadPx) ? Number(leftPadPx) : 0);
                const safeRightPadSeconds = Math.max(0, Number.isFinite(rightPadSeconds) ? Number(rightPadSeconds) : 0);
                const startLeadInSec = getStartLeadInSec();
                const safeCompositionDurationSec = Math.max(
                    0,
                    Number.isFinite(compositionDurationSec) ? Number(compositionDurationSec) : 0,
                );
                const availableContentWidthPx = Math.max(1, safeContainerWidthPx - safeLeftPadPx);
                const fitPxPerSec = availableContentWidthPx / (safeDurationSec + safeRightPadSeconds + startLeadInSec);

                // Default zoom: start slightly zoomed in (show ~35s) instead of always fit-to-view.
                // Only apply once per composition session, and only if the user hasn't zoomed yet.
                if (
                    !TimelineStore.didApplyDefaultZoom &&
                    Math.abs(zoomPercent - MIN_ZOOM_PERCENT) < 1e-6 &&
                    safeCompositionDurationSec > 0
                ) {
                    const targetWindowSec = Math.min(safeDurationSec, DEFAULT_VISIBLE_WINDOW_SEC);
                    const desiredPxPerSec = availableContentWidthPx / (targetWindowSec + safeRightPadSeconds + startLeadInSec);
                    const desiredZoomPercent = computeZoomPercentForPxPerSec(desiredPxPerSec, fitPxPerSec);

                    TimelineStore.setDidApplyDefaultZoom(true);
                    if (desiredZoomPercent > zoomPercent + 1e-6) {
                        const tracksContainer = timelineTracksRef.current as any;
                        TimelineStore.setBaseStartSec(getBaseStartFloorSec());
                        TimelineStore.setScrollLeftPx(0);
                        if (tracksContainer) {
                            isRecenteringRef.current = true;
                            tracksContainer.scrollLeft = 0;
                            handleScroll();
                            window.requestAnimationFrame(() => {
                                isRecenteringRef.current = false;
                            });
                        }
                        TimelineStore.setZoomPercent(desiredZoomPercent);
                        return;
                    }
                }

                const nextPxPerSec = computePxPerSecForZoomPercent(zoomPercent, fitPxPerSec);
                const nextZoom = nextPxPerSec / TIMELINE_RULER_TICK_WIDTH_PX;

                const prevZoom = TimelineStore.zoom;
                if (Math.abs(nextZoom - prevZoom) < 1e-12) return;

                // Anchor: keep the viewport center time stable through zoom changes.
                const container = timelineTracksRef.current as any;
                if (!container) {
                    TimelineStore.setZoom(nextZoom);
                    return;
                }

                const prevPxPerSec = TimelineStore.pxPerSec;
                const prevLeftPadPx = TimelineStore.leftPadPx;
                const viewportWidthPx = Number(container.clientWidth ?? safeContainerWidthPx);
                const anchorX = viewportWidthPx / 2;
                const prevScrollLeftPx = Math.max(0, Number(container.scrollLeft ?? TimelineStore.scrollLeftPx ?? 0));
                const prevTrackScrollLeftPx = Math.max(0, prevScrollLeftPx - prevLeftPadPx);
                const prevBaseStartSec = TimelineStore.baseStartSec ?? getBaseStartFloorSec();
                const baseStartFloorSec = getBaseStartFloorSec();
                const prevRightPadPx = safeRightPadSeconds * prevPxPerSec;
                const prevFullTrackContentWidthPx = (safeDurationSec + startLeadInSec) * prevPxPerSec + prevRightPadPx;
                const prevTrackContentWidthPx = Math.min(prevFullTrackContentWidthPx, MAX_TRACK_CONTENT_WIDTH_PX);
                const prevMaxTrackScrollLeftPx = Math.max(0, prevTrackContentWidthPx - viewportWidthPx);
                const prevMaxScrollLeftPx = prevLeftPadPx + prevMaxTrackScrollLeftPx;
                const pinnedStart =
                    prevScrollLeftPx <= 1 ||
                    (prevTrackScrollLeftPx <= 1 && prevBaseStartSec <= baseStartFloorSec + 1e-6);
                const pinnedEnd = Math.abs(prevScrollLeftPx - prevMaxScrollLeftPx) <= 1;
                const anchorSec =
                    prevPxPerSec > 0
                        ? prevBaseStartSec + (prevTrackScrollLeftPx + anchorX) / prevPxPerSec
                        : prevBaseStartSec;

                TimelineStore.setZoom(nextZoom);

                // Fit view: reset to start (no horizontal scrolling needed).
                if (zoomPercent <= MIN_ZOOM_PERCENT + 1e-6) {
                    isRecenteringRef.current = true;
                    TimelineStore.setBaseStartSec(baseStartFloorSec);
                    container.scrollLeft = 0;
                    handleScroll();
                    window.requestAnimationFrame(() => {
                        isRecenteringRef.current = false;
                    });
                    return;
                }

                if (pinnedStart) {
                    isRecenteringRef.current = true;
                    TimelineStore.setBaseStartSec(baseStartFloorSec);
                    container.scrollLeft = 0;
                    handleScroll();
                    window.requestAnimationFrame(() => {
                        isRecenteringRef.current = false;
                    });
                    return;
                }

                if (pinnedEnd) {
                    positionToTime(safeDurationSec, viewportWidthPx);
                    return;
                }

                positionToTime(anchorSec, anchorX);
            },
            { fireImmediately: true },
        );

        const dispose = reaction(
            () => ({
                isPlaying: CompositionStateStore.isPlaying,
                currentTime: CompositionStateStore.currentTime,
            }),
            ({ isPlaying, currentTime }) => {
                ensureTimeVisible(currentTime, isPlaying ? "playing" : "seeking");
            },
            { fireImmediately: true },
        );

        return () => {
            disposeZoom();
            dispose();
        };
    }, []);
    useEffect(() => {
        // initialize playhead position relative to left pad on mount
        handleScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        // Keep playhead offset aligned when zoom changes (left pad is in px).
        handleScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoom]);

    // Ctrl+wheel zoom (non-passive): prevent browser/page scroll/zoom while zooming timeline.
    useEffect(() => {
        const containerEl = timelineContainerRef.current;
        if (!containerEl) return;

        const onWheel = (event: WheelEvent) => {
            const tracksContainer = timelineTracksRef.current as any;
            if (!tracksContainer) return;

            event.stopPropagation();

            if (event.ctrlKey) {
                event.preventDefault();
                const deltaY = Number(event.deltaY ?? 0);
                const deltaYpx =
                    event.deltaMode === 1
                        ? deltaY * 16
                        : event.deltaMode === 2
                            ? deltaY * Math.max(1, Number(tracksContainer.clientHeight ?? 0))
                            : deltaY;

                const deltaPercentRaw = -deltaYpx * ZOOM_WHEEL_SENSITIVITY;
                const deltaPercent = clamp(deltaPercentRaw, -ZOOM_WHEEL_MAX_DELTA_PERCENT, ZOOM_WHEEL_MAX_DELTA_PERCENT);
                const desiredZoomPercent = TimelineStore.zoomPercent + deltaPercent + wheelZoomRemainderRef.current;

                TimelineStore.setZoomPercent(desiredZoomPercent);

                const appliedZoomPercent = TimelineStore.zoomPercent;
                if (appliedZoomPercent <= MIN_ZOOM_PERCENT + 1e-6 || appliedZoomPercent >= MAX_ZOOM_PERCENT - 1e-6) {
                    wheelZoomRemainderRef.current = 0;
                } else {
                    wheelZoomRemainderRef.current = desiredZoomPercent - appliedZoomPercent;
                }
                return;
            }

            // Regular pan/scroll (horizontal).
            tracksContainer.scrollLeft += event.deltaY + event.deltaX;
        };

        containerEl.addEventListener("wheel", onWheel, { passive: false });
        return () => {
            containerEl.removeEventListener("wheel", onWheel as any);
        };
    }, []);

    // Separate regular tracks from script layer - script layer renders at bottom with higher z-index
    const allLayersOrder = [...orderedLayerIds].reverse();
    const regularLayersOrder = scriptLayerId 
        ? allLayersOrder.filter(id => id !== scriptLayerId)
        : allLayersOrder;
    const hasScriptLayer = scriptLayerId && orderedLayerIds.includes(scriptLayerId);
    
    const durationSec = Number(duration ?? 0);
    const leftPadPx = TimelineStore.leftPadPx;
    const rightPadPx = TimelineStore.rightPadPx;
    const pxPerSec = TimelineStore.pxPerSec;
    const startLeadInSec = getStartLeadInSec();
    const fullTrackContentWidthPx = (durationSec + startLeadInSec) * pxPerSec + rightPadPx;
    const trackContentWidthPx = Math.min(fullTrackContentWidthPx, MAX_TRACK_CONTENT_WIDTH_PX);
    const tracksWidth = trackContentWidthPx + leftPadPx;

    return (
        <div id={TIMELINE_ELEMENT_ID} className="timeline libretto-timeline" onContextMenu={(e) => e.preventDefault()}>
            <div id={TIMELINE_CONTROLS_ELEMENT_ID} className="timeline__controls">
                <Controls timelineHidden={timelineHidden} toggleTimelineHide={toggleTimelineHide} disabled={disabled}/>
            </div>

            <div
                className="timeline__container"
                ref={timelineContainerRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onContextMenu={handleTimelineContextMenu}
            >
                {/*{layersOrder.length === 0 && <EmptyTimelineButton/>}*/}

                <div className="timeline__canvas-ruler">
                    <CanvasTimeRuler />
                </div>

                {/*/!* Fixed zero marker at 0:00 *!/*/}
                {/*<div className="timeline__zero" style={{ transform: `translateX(calc(-50% + ${playheadX}px))` }}>*/}
                {/*    <div className="timeline__zero-line"></div>*/}
                {/*</div>*/}

                <div className="timeline__playhead" style={{ transform: `translateX(calc(-50% + ${playheadX}px))` }}>
                    {/* @ts-ignore */}
                    <Playhead containerElement={document} />
                </div>

                {cursorTime != null && (
                    <div className="timeline__cursor-playhead" style={{ transform: `translateX(calc(-50% + ${playheadX}px))` }}>
                        <div className="cursor-playhead" style={{ transform: `translateX(${convertAbsTimeToUnits(cursorTime)}px)` }}>
                            <div className="cursor-playhead__label">{secondsToTimecode(cursorTime)}</div>
                            <div className="cursor-playhead__line" />
                        </div>
                    </div>
                )}

                <div className="timeline__divider-top">{renderTrackDivider(0)}</div>

                <div
                    id={TIMELINE_TRACKS_CONTAINER_ID}
                    data-tour="timeline-waveform-area"
                    className="timeline__tracks"
                    onMouseDown={(e) => {
                        // Only handle left-click
                        if (e.button !== 0) return;

                        // Calculate clicked time from position (accounting for scroll and left padding)
                        const container = timelineTracksRef.current;
                        if (container) {
                            const rect = container.getBoundingClientRect();
                            const scrollLeft = container.scrollLeft;
                            const clickX = e.clientX - rect.left + scrollLeft - leftPadPx;
                            const clickedTimeRaw = convertUnitsToTime(Math.max(0, clickX));
                            const maxTime = Number(CompositionStateStore.duration ?? 0);
                            const clickedTime = Math.max(
                                0,
                                Number.isFinite(maxTime) && maxTime > 0
                                    ? Math.min(maxTime, clickedTimeRaw)
                                    : clickedTimeRaw,
                            );
                            CompositionService.seek(clickedTime);
                        }

                        // Clear selection when clicking empty space
                        ApplicationStore.clearSelectedClipId();
                    }}
                    ref={timelineTracksRef}
                >
                    {/* Regular tracks - rendered behind the script layer */}
                    {regularLayersOrder.length > 0 && (
                        <div className="tracks tracks--regular" style={{ minWidth: `${tracksWidth}px`, display: 'flex' }}>
                            <div style={{ minWidth: `${leftPadPx}px` }} />
                            <div style={{ flex: 1 }}>
                                {regularLayersOrder.map((layerId, index) => (
                                    // @ts-ignore
                                    <Track key={layerId} layerId={layerId}>
                                        {index < regularLayersOrder.length - 1 && renderTrackDivider(index + 1)}
                                    </Track>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Void container - provides drop zone and spacing, placed before script layer */}
                    <div className="timeline__void-container">{renderTrackDivider(allLayersOrder.length)}</div>

                    {/* Script layer track - sticky at the bottom with higher z-index */}
                    {/* Other tracks scroll behind this when timeline is scrolled down */}
                    {hasScriptLayer && (
                        <div className="tracks tracks--script-layer" style={{ minWidth: `${tracksWidth}px`, display: 'flex' }}>
                            <div style={{ minWidth: `${leftPadPx}px` }} />
                            <div style={{ flex: 1 }}>
                                {/* @ts-ignore */}
                                <Track key={scriptLayerId} layerId={scriptLayerId} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default Timeline;
